import { useEffect, useState } from 'react'
import { updateProject } from '../api/client'
import { useUIStore } from '../store/uiStore'
import './NewProjectModal.css'

interface EditProjectModalProps {
  projectId: string | null
  initialName: string
  initialDescription: string
  onUpdated?: () => void
}

export default function EditProjectModal({ projectId, initialName, initialDescription, onUpdated }: EditProjectModalProps) {
  const isOpen = useUIStore((s) => s.modals.editProject)
  const closeModal = useUIStore((s) => s.closeModal)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when modal opens with new initial values
  useEffect(() => {
    if (isOpen) {
      setName(initialName)
      setDescription(initialDescription)
    }
  }, [isOpen, initialName, initialDescription])

  if (!isOpen || !projectId) return null

  const handleClose = () => {
    closeModal('editProject')
  }

  const handleSubmit = async () => {
    const n = name.trim()
    if (!n) {
      alert('Please enter a project name.')
      return
    }
    setIsSubmitting(true)
    try {
      await updateProject(projectId, n, description.trim() || undefined)
      closeModal('editProject')
      onUpdated?.()
    } catch (e) {
      console.error('Failed to update project:', e)
      alert(`Failed to update project: ${e instanceof Error ? e.message : 'Unknown error'}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content new-project-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit project</h2>
          <button className="modal-close" onClick={handleClose} type="button">
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="np-field">
            <label>Project name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Nuclear risk" />
          </div>
          <div className="np-field">
            <label>Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this project about?"
              rows={3}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-primary" type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save changes'}
          </button>
          <button className="btn-secondary" type="button" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
